import ChartColors, {
  pieColors,
} from "../../../../../../config/live.chart.colors";
import ecpImg from "./final-logo.png";
import jsPDFNamespace from "jspdf";
const jsPDF = jsPDFNamespace.jsPDF || jsPDFNamespace;
let generating = false;

const pad = (n) => String(n).padStart(2, "0");

/* FORMAT TIMESTAMP (NO TZ SHIFT) */
const fmtLocalTsString = (s) => {
  if (typeof s !== "string") return String(s ?? "--");
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/);
  if (!m) return s;
  const [, y, mo, d, hh, mm, ss] = m;
  return `${d}/${mo}/${y} ${hh}:${mm}:${ss}`;
};

const fmtIsoTsNoMs = (s) => {
  if (typeof s !== "string") return String(s ?? "--");
  const m = s.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(?:[.,]\d+)?/
  );
  if (!m) return s;
  const [, y, mo, d, hh, mm, ss] = m;
  return `${y}-${mo}-${d}T${hh}:${mm}:${ss}`;
};

export const generatedTimestamp = () => {
  const d = new Date();
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(
    d.getUTCDate()
  )} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(
    d.getUTCSeconds()
  )}`;
};

const hexToRgb = (hex) => {
  const h = hex.replace("#", "");
  const bigint = parseInt(h, 16);
  return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
};

function drawBarChartChartJsLike(doc, items, x, y, w, h, opts = {}) {
  const {
    legendW = 80,

    padLeft = 22,
    padRight = 6,
    padTop = 6,
    padBottom = 12,

    gap = 3,
    colors = [],
    defaultColor = [120, 120, 120],

    axisColor = 70,
    axisLineWidth = 0.6,
    gridColor = 210,
    gridLineWidth = 0.3,

    ticks = 5,
    tickFontSize = 7,
    tickFormat = (v) => String(v),
    valueDecimals = 2,

    legendFontSize = 8,
    legendLineH = 5,
    legendDotSize = 3,

    showBarValues = false,
    barValueFontSize = 7,

    sort = "desc",
    greyText = 150,
    showNonNumericInLegend = false,
    showNonNumericLegendValue = true,
  } = opts;

  const normalized = (Array.isArray(items) ? items : []).map((it) => {
    const label = String(
      it?.label ?? it?.name ?? it?.tag?.label ?? it?.tag?.value ?? "-"
    );
    const raw = it?.finalValue ?? it?.value;

    const isPlaceholder =
      raw == null || raw === "" || String(raw).trim() === "--";

    const num = isPlaceholder ? NaN : Number(raw);
    return { label, raw, value: Number.isFinite(num) ? num : null };
  });

  const numeric = normalized.filter((p) => p.value != null);
  const nonNumeric = normalized.filter((p) => p.value == null);

  if (!numeric.length) {
    const plotW = Math.max(30, w - legendW);
    const legendX = x + plotW + 4;

    const px = x + padLeft;
    const py = y + padTop;
    const pw = plotW - padLeft - padRight;
    const ph = h - padTop - padBottom;

    const baseY = py + ph;
    const baseX = px;

    doc.setDrawColor(axisColor);
    doc.setLineWidth(axisLineWidth);
    doc.line(baseX, py, baseX, baseY);
    doc.line(baseX, baseY, baseX + pw, baseY);

    const cx = baseX + pw / 2;
    const cy = py + ph / 2;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(28);
    doc.setTextColor(190);
    doc.text("N/A", cx, cy, { align: "center" });
    doc.setTextColor(0);
    doc.setFontSize(tickFontSize);

    if (showNonNumericInLegend && nonNumeric.length) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(legendFontSize);

      const fmtRaw = (raw) => {
        if (raw == null || raw === "" || String(raw).trim() === "--")
          return "--";
        return String(raw);
      };

      let ly = y + 10;
      for (let i = 0; i < nonNumeric.length; i++) {
        if (ly + legendLineH > y + h) break;

        const maxChars = 26;
        const name =
          nonNumeric[i].label.length > maxChars
            ? nonNumeric[i].label.slice(0, maxChars - 1) + "…"
            : nonNumeric[i].label;

        doc.setTextColor(greyText);
        doc.text(
          showNonNumericLegendValue
            ? `${name}: ${fmtRaw(nonNumeric[i].raw)}`
            : `${name}`,
          legendX,
          ly
        );
        ly += legendLineH;
      }
      doc.setTextColor(0);
    }

    return h;
  }

  if (!numeric.length) return 0;

  if (sort === "asc") numeric.sort((a, b) => a.value - b.value);
  else if (sort === "desc") numeric.sort((a, b) => b.value - a.value);

  const parsed = numeric;
  const finite = parsed;
  const rawMax = Math.max(...finite.map((p) => p.value), 1);

  const niceMax = (max) => {
    const exp = Math.floor(Math.log10(max));
    const base = Math.pow(10, exp);
    const frac = max / base;
    let niceFrac = 1;
    if (frac <= 1) niceFrac = 1;
    else if (frac <= 2) niceFrac = 2;
    else if (frac <= 5) niceFrac = 5;
    else niceFrac = 10;
    return niceFrac * base;
  };

  const maxV = niceMax(rawMax);
  const step = maxV / ticks;

  const plotW = Math.max(30, w - legendW);
  const legendX = x + plotW + 4;

  const px = x + padLeft;
  const py = y + padTop;
  const pw = plotW - padLeft - padRight;
  const ph = h - padTop - padBottom;

  const baseY = py + ph;
  const baseX = px;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(tickFontSize);
  doc.setTextColor(0);

  for (let i = 0; i <= ticks; i++) {
    const v = step * i;
    const ty = baseY - (v / maxV) * ph;

    doc.setDrawColor(gridColor);
    doc.setLineWidth(gridLineWidth);
    doc.line(baseX, ty, baseX + pw, ty);

    doc.setTextColor(60);
    doc.text(tickFormat(Math.round(v * 1000) / 1000), baseX - 4, ty + 2, {
      align: "right",
    });
  }

  doc.setDrawColor(axisColor);
  doc.setLineWidth(axisLineWidth);

  doc.line(baseX, py, baseX, baseY);

  doc.line(baseX, baseY, baseX + pw, baseY);

  const n = parsed.length;
  const MIN_BAR_W = 6;
  let barW = (pw - gap * (n + 1)) / n;

  if (barW < MIN_BAR_W) {
    barW = MIN_BAR_W;
  }
  const totalBarsW = n * barW + gap * (n + 1);
  const offsetX = totalBarsW < pw ? (pw - totalBarsW) / 2 : 0;

  for (let i = 0; i < n; i++) {
    const v = parsed[i].value;
    const barH = (v / maxV) * ph;

    const bx = baseX + offsetX + gap + i * (barW + gap);

    const by = baseY - barH;

    const c = colors[i] || defaultColor;
    doc.setFillColor(c[0], c[1], c[2]);
    doc.rect(bx, by, barW, barH, "F");

    if (showBarValues) {
      const val = Number.isInteger(v) ? String(v) : v.toFixed(valueDecimals);
      doc.setFontSize(barValueFontSize);
      doc.setTextColor(50);
      doc.text(val, bx + barW / 2, by - 1, { align: "center" });
      doc.setFontSize(tickFontSize);
    }
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(legendFontSize);
  doc.setTextColor(0);

  const fmtVal = (p) => {
    if (p.value == null) return "--";
    if (Number.isInteger(p.value)) return String(p.value);
    return p.value.toFixed(valueDecimals);
  };

  let ly = y + 10;

  for (let i = 0; i < parsed.length; i++) {
    if (ly + legendLineH > y + h) break;

    const c = colors[i] || defaultColor;
    doc.setFillColor(c[0], c[1], c[2]);
    doc.rect(
      legendX,
      ly - legendDotSize + 1,
      legendDotSize,
      legendDotSize,
      "F"
    );

    const maxChars = 26;
    const name =
      parsed[i].label.length > maxChars
        ? parsed[i].label.slice(0, maxChars - 1) + "…"
        : parsed[i].label;

    doc.setTextColor(30);
    doc.text(`${name}: ${fmtVal(parsed[i])}`, legendX + legendDotSize + 3, ly);

    ly += legendLineH;
  }

  if (showNonNumericInLegend && nonNumeric.length) {
    const fmtRaw = (raw) => {
      if (raw == null || raw === "" || String(raw).trim() === "--") return "--";
      return String(raw);
    };

    for (let i = 0; i < nonNumeric.length; i++) {
      if (ly + legendLineH > y + h) break;

      const maxChars = 26;
      const name =
        nonNumeric[i].label.length > maxChars
          ? nonNumeric[i].label.slice(0, maxChars - 1) + "…"
          : nonNumeric[i].label;

      doc.setTextColor(greyText);
      doc.text(
        showNonNumericLegendValue
          ? `${name}: ${fmtRaw(nonNumeric[i].raw)}`
          : `${name}`,
        legendX,
        ly
      );
      ly += legendLineH;
    }
    doc.setTextColor(0);
  }

  return h;
}

export async function generateDemoPDF(params = {}) {
  const {
    rows: passedRows = [],
    title,
    prologue,
    epilogue,
    headerTimestamp,
    headerTsStyle = "local",
    returnDataUri = false,
    dFlag = true,
    barChart = null,
  } = params;
  if (generating) return null;
  generating = true;

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const MARGIN_X = 20;
  const PARA_PAD = 8;
  const AFTER_TABLE_PAD = 0;
  const LINE_H = 5;
  const ROW_H = 8;
  const TABLE_W = 170;
  const FOOTER_Y = pageHeight - 12;
  const SAFE_BOTTOM = FOOTER_Y - 6;

  const HEADER_TS_Y = 21;
  const BODY_TOP_MIN = HEADER_TS_Y + 6 /*padding*/ + 6; /*row rect offset*/

  const col1X = MARGIN_X;
  const col2X = 120;

  const drawHeader = () => {
    const imgWidth = 340 / 6.5,
      imgHeight = 65 / 6.5;
    doc.addImage(ecpImg, "PNG", 4, 4, imgWidth, imgHeight);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(title || "REPORT", pageWidth / 2, 15, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);

    const headerLine =
      headerTsStyle === "iso"
        ? fmtIsoTsNoMs(headerTimestamp)
        : fmtLocalTsString(headerTimestamp);
    doc.text(headerLine, pageWidth / 2, 21, { align: "center" });
  };

  const drawFooter = (pageNo, totalPages) => {
    doc.setFontSize(8);
    doc.text("© Electropneumatic S.A.", 20, FOOTER_Y);
    doc.text(`Page ${pageNo} / ${totalPages}`, pageWidth - 30, FOOTER_Y);
  };

  const newPage = () => {
    doc.addPage();
    drawHeader();
    return BODY_TOP_MIN;
  };

  drawHeader();

  let cursorY = BODY_TOP_MIN;

  if (prologue && String(prologue).trim()) {
    doc.setFontSize(10);
    const lines = doc.splitTextToSize(
      String(prologue),
      pageWidth - 2 * MARGIN_X
    );

    const textH = doc.getTextDimensions(lines).h;

    const blockHeight = textH + PARA_PAD;
    if (cursorY + blockHeight > SAFE_BOTTOM) {
      cursorY = newPage();
    }

    const top = cursorY;
    doc.text(lines, MARGIN_X, top, { align: "left" });

    cursorY = top + textH + PARA_PAD;
  }

  let y = Math.max(cursorY, BODY_TOP_MIN);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);

  const rows = Array.isArray(passedRows)
    ? passedRows.map((r) => {
        const unit = r?.unit;
        const isTime = unit === "startTs" || unit === "endTs";

        const looksLikeTs =
          typeof r?.value === "string" &&
          /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(?:[.,]\d+)?/.test(
            r.value
          );

        const baseLabel =
          r?.name && String(r.name).trim().length > 0
            ? r.name
            : r?.tag?.label || r?.tag?.value || "-";
        const label = unit && !isTime ? `${baseLabel} (${unit})` : baseLabel;

        let value = r?.value;

        if ((isTime || looksLikeTs) && typeof value === "string") {
          value = fmtLocalTsString(value);
        } else if (value != null) {
          const num = Number(value);
          if (Number.isFinite(num) && Math.abs(num) < 1e12) {
            value = Number.isInteger(num) ? num.toString() : num.toFixed(2);
          }
        }
        if (value == null || value === "" || value === "NaN") value = "--";

        let valueStr = String(value);
        const isBold = r?.isBold || false;

        return [label, valueStr, isBold];
      })
    : [];

  const drawRow = (key, value, yrow, isBold) => {
    const isPlaceholder = String(value).trim() === "--";

    doc.setDrawColor(0);
    doc.rect(col1X - 2, yrow - 6, TABLE_W, ROW_H);
    doc.line(col2X - 2, yrow - 6, col2X - 2, yrow + ROW_H - 6);

    doc.setFont("helvetica", isBold ? "bold" : "normal");
    doc.setTextColor(0);
    doc.text(key, col1X, yrow - 1);

    doc.setFont("helvetica", !isPlaceholder && isBold ? "bold" : "normal");
    doc.setTextColor(isPlaceholder ? 150 : 0);
    doc.text(String(value), col2X, yrow - 1);

    doc.setTextColor(0);
  };

  rows.forEach(([key, value, isBold], i) => {
    if (y + ROW_H > SAFE_BOTTOM) {
      cursorY = newPage();
      y = cursorY;
    }
    drawRow(key, value, y, isBold);
    y += ROW_H;
  });

  const barItems = Array.isArray(barChart) ? barChart : [];

  const numericCount = barItems.filter((it) => {
    const raw = it?.finalValue ?? it?.value;
    if (raw == null || raw === "" || String(raw).trim() === "--") return false;
    const n = Number(raw);
    return Number.isFinite(n);
  }).length;

  if (barItems.length && (numericCount > 0 || numericCount === 0)) {
    const paletteHex = pieColors;
    const colors = paletteHex.map(hexToRgb);

    const CHART_MIN_H = 52;
    const CHART_MAX_H = 82;
    const chartH = Math.min(
      CHART_MAX_H,
      Math.max(CHART_MIN_H, 34 + barItems.length * 2)
    );

    const CHART_MARGIN_X = 10;
    const CHART_W = pageWidth - CHART_MARGIN_X * 2;

    if (y + chartH > SAFE_BOTTOM) {
      cursorY = newPage();
      y = cursorY;
    }

    drawBarChartChartJsLike(doc, barItems, CHART_MARGIN_X, y, CHART_W, chartH, {
      colors,
      legendW: 62,
      legendFontSize: 9,
      legendLineH: 4,
      ticks: 4,
      padLeft: 18,
      padRight: 4,
      padTop: 3,
      padBottom: 9,
      showBarValues: false,
      sort: "desc",
      showNonNumericInLegend: true,
      showNonNumericLegendValue: false,
    });

    y += chartH + 8;
  }

  if (epilogue && String(epilogue).trim()) {
    const lines = doc.splitTextToSize(
      String(epilogue),
      pageWidth - 2 * MARGIN_X
    );
    let i = 0;
    let lineY = y + AFTER_TABLE_PAD + PARA_PAD;
    while (i < lines.length) {
      if (lineY + LINE_H > SAFE_BOTTOM) {
        cursorY = newPage();
        lineY = cursorY + PARA_PAD;
      }
      doc.text(lines[i], MARGIN_X, lineY, { align: "left" });
      lineY += LINE_H;
      i += 1;
    }
  }

  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    drawFooter(p, totalPages);
  }

  const fileName = `${
    title ? title.replace(/[^a-zA-Z0-9-_]/g, "_") : "report"
  }.pdf`;
  if (returnDataUri) {
    const dataUri = doc.output("datauristring");
    console.log("Generated PDF Data URI:", dataUri.slice(0, 100) + "...");
    if (dFlag) doc.save(fileName);
    generating = false;
    return { dataUri, fileName };
  }
  console.log("Generated PDF File:", fileName);
  doc.save(fileName);
  generating = false;
  return fileName;
}
